import { CoreTooltip } from '@featuring-corp/components';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import React from 'react';

import {
	barRate,
	categoryClass,
	chartBarClass,
	chartBarContainerClass,
	chartBarHeight,
	chartBarWidth,
	chartBarWrapperClass,
	chartColor,
	chartContainerClass,
	containerClass,
	labelClass,
	labelContainerClass,
	valueClass,
} from './style.css';

export interface SymmetricVerticalBarChartDataItem {
	value?: string | number;
	valueColor?: string;
	barColor?: string;
	tooltip?: string;
	valuePercentage?: number;
	color?: string;
}

export interface SymmetricVerticalBarChartProps {
	categories?: string[];
	categoriesColor?: string;
	topDataLabel?: string;
	bottomDataLabel?: string;
	topData?: SymmetricVerticalBarChartDataItem[];
	bottomData?: SymmetricVerticalBarChartDataItem[];
	barWidth?: number;
	barHeight?: number;

	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const SymmetricVerticalBarChart = ({
	categories,
	categoriesColor,
	topDataLabel,
	bottomDataLabel,
	barWidth = 58,
	barHeight = 65,

	isLoading,
	isError,
	lackOfPermission,
	...props
}: SymmetricVerticalBarChartProps) => {
	const topData = props?.topData
		? isLoading || isError
			? props.topData.map((data) => ({ ...data, valuePercentage: 0 }))
			: lackOfPermission
			? props.topData.map((data) => ({ ...data, valuePercentage: 0.5 }))
			: props.topData
		: [];

	const bottomData = props?.bottomData
		? isLoading || isError
			? props.bottomData.map((data) => ({ ...data, valuePercentage: 0 }))
			: lackOfPermission
			? props.bottomData.map((data) => ({ ...data, valuePercentage: 0.5 }))
			: props.bottomData
		: undefined;

	return (
		<div className={containerClass}>
			{(topDataLabel || bottomDataLabel) && (
				<div className={labelContainerClass}>
					{topDataLabel && <div className={labelClass}>{topDataLabel}</div>}

					{bottomDataLabel && <div className={labelClass}>{bottomDataLabel}</div>}
				</div>
			)}

			<div className={chartContainerClass}>
				{categories?.map((category, idx) => (
					<div key={idx} className={chartBarContainerClass}>
						{topData?.[idx]?.value && (
							<div
								className={valueClass}
								style={assignInlineVars({ [chartColor]: topData?.[idx]?.valueColor })}
							>
								{topData?.[idx].value}
							</div>
						)}

						{topData?.[idx]?.valuePercentage !== undefined && (
							<CoreTooltip
								disabled={!topData?.[idx]?.tooltip}
								text={topData?.[idx]?.tooltip}
							>
								<div
									className={chartBarWrapperClass({ placement: 'top' })}
									style={assignInlineVars({
										[chartBarWidth]: `${barWidth}px`,
										[chartBarHeight]: `${barHeight}px`,
									})}
								>
									<div
										className={chartBarClass}
										style={assignInlineVars({
											[chartColor]: topData?.[idx]?.barColor,
											[barRate]: topData?.[idx]?.valuePercentage?.toString(),
											[chartBarWidth]: `${barWidth}px`,
										})}
									/>
								</div>
							</CoreTooltip>
						)}

						{category && (
							<div
								className={categoryClass}
								style={assignInlineVars({ [chartColor]: categoriesColor })}
							>
								{category}
							</div>
						)}

						{bottomData?.[idx]?.valuePercentage !== undefined && (
							<CoreTooltip
								disabled={!bottomData?.[idx]?.tooltip}
								text={bottomData?.[idx]?.tooltip}
								placement="bottom"
							>
								<div
									className={chartBarWrapperClass({ placement: 'bottom' })}
									style={assignInlineVars({
										[chartBarWidth]: `${barWidth}px`,
										[chartBarHeight]: `${barHeight}px`,
									})}
								>
									<div
										className={chartBarClass}
										style={assignInlineVars({
											[chartColor]: bottomData?.[idx]?.barColor,
											[barRate]: bottomData?.[idx]?.valuePercentage?.toString(),
											[chartBarWidth]: `${barWidth}px`,
										})}
									/>
								</div>
							</CoreTooltip>
						)}

						{bottomData?.[idx]?.value && (
							<div
								className={valueClass}
								style={assignInlineVars({ [chartColor]: bottomData?.[idx]?.valueColor })}
							>
								{bottomData?.[idx]?.value}
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

export default SymmetricVerticalBarChart;
